import { faArrowRight, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { PropsWithChildren, ReactElement } from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { isFunction, isObject } from 'lodash-es';
import classNames from '../../utils/classNames';
import { useTimedNotificationDismiss } from './useTimedNotificationDismiss';

export enum NOTIFICATION_VARIANT {
    SUCCESS = 'success',
    ERROR = 'error',
    WARNING = 'warning',
}

const variantStyleMap = {
    [NOTIFICATION_VARIANT.SUCCESS]: 'bg-success text-white',
    [NOTIFICATION_VARIANT.ERROR]: 'bg-error text-white',
    [NOTIFICATION_VARIANT.WARNING]: 'bg-warning text-white',
};

interface NotificationType {
    message: string | ReactElement;
    variant?: NOTIFICATION_VARIANT;
    href?: string | null;
    scrollToTop?: boolean;
    autoDismiss?: boolean;
}

interface NotificationWithId extends NotificationType {
    id: number;
}

interface NotificationsContextType {
    notifications: NotificationWithId[];
    addNotification: (notification: NotificationType) => { remove: () => void };
    removeNotification: (toBeRemoved: NotificationWithId | number) => void;
    clearNotifications: () => void;
}

const NotificationsContext = createContext<NotificationsContextType | null>(null);

// Temporary dirty hack to use the new React notification system outside
// of React (for example on the not yet converted overview page).
// Note: With nested providers (e.g. modal + page), we only want the page-level
// provider to register itself as the global target for this hack.
// Issue about converting the overview page:
// https://github.com/omnidots/website/issues/5579
let addNotificationRef: ((notification: NotificationType) => { remove: () => void }) | null = null;
export function addNotificationHack(notification: NotificationType) {
    isFunction(addNotificationRef) && addNotificationRef(notification);
}

function useNotificationsProvider(registerGlobalRef: boolean) {
    const [notifications, setNotifications] = useState<Array<NotificationWithId>>([]);
    const nextNotificationIdRef = useRef(0);

    const removeNotification = useCallback(
        (toBeRemoved: NotificationWithId | number) => {
            setNotifications((current) =>
                current.filter((notification, index) =>
                    isObject(toBeRemoved)
                        ? // Filter out the given notification object.
                          notification !== toBeRemoved
                        : // Filter out the given index.
                          index !== toBeRemoved
                )
            );
        },
        [setNotifications]
    );

    const addNotification = useCallback(
        (notification: NotificationType) => {
            const id = nextNotificationIdRef.current;
            nextNotificationIdRef.current = id + 1;
            const notificationWithDefaults: NotificationWithId = {
                id,
                variant: NOTIFICATION_VARIANT.SUCCESS,
                scrollToTop: false,
                ...notification,
            };
            setNotifications((current) => [...current, notificationWithDefaults]);

            if (notificationWithDefaults.scrollToTop) {
                window.scrollTo(0, 0);
            }

            return {
                remove() {
                    return removeNotification(notificationWithDefaults);
                },
            };
        },
        [setNotifications, removeNotification]
    );

    // Part of the hack mentioned above. Only one provider should register as the
    // global target to avoid nested providers hijacking page-level notifications.
    useEffect(() => {
        if (!registerGlobalRef) return undefined;

        addNotificationRef = addNotification;

        return () => {
            addNotificationRef = null;
        };
    }, [addNotification, registerGlobalRef]);

    const clearNotifications = useCallback(() => {
        setNotifications((current) => current.filter((_, _index) => false));
    }, [setNotifications]);

    return {
        notifications,
        addNotification,
        removeNotification,
        clearNotifications,
    };
}

export function useNotifications() {
    const context = useContext(NotificationsContext);

    if (!context) {
        throw new Error('No notification context available.');
    }

    return context;
}

export function NotificationsProvider({
    children,
    registerGlobalRef = true,
}: PropsWithChildren<{ registerGlobalRef?: boolean }>): JSX.Element {
    const fact = useNotificationsProvider(registerGlobalRef);
    return <NotificationsContext.Provider value={fact}>{children}</NotificationsContext.Provider>;
}

interface NotificationProps {
    notification: NotificationWithId;
    message: string | ReactElement;
    variant?: NOTIFICATION_VARIANT;
    href?: string | null;
    autoDismiss?: boolean;
    textSize?: string;
}

export function Notification({
    notification,
    message,
    variant,
    href,
    autoDismiss,
    textSize = 'text-sm',
}: NotificationProps): JSX.Element {
    const { removeNotification } = useNotifications();
    const dismissNotification = useCallback(() => {
        removeNotification(notification);
    }, [notification, removeNotification]);
    const { autoDismissMs, isClosing, isOverlayShrinking } = useTimedNotificationDismiss({
        autoDismiss,
        onDismiss: dismissNotification,
    });

    const buttonProps = href
        ? { href }
        : {
              type: 'button' as const,
              onClick() {
                  dismissNotification();
              },
          };
    const buttonIcon = href ? faArrowRight : faXmark;
    const Button = href ? 'a' : 'button';

    return (
        <li
            className={classNames(
                'a-underline relative mb-2 flex justify-between gap-x-4 overflow-hidden rounded p-1.5 transition-[opacity,transform] duration-200 ease-out',
                variant ? variantStyleMap[variant] : '',
                isClosing ? '-translate-y-1 opacity-0' : 'translate-y-0 opacity-100',
                textSize
            )}
        >
            {autoDismiss ? (
                <span
                    aria-hidden={true}
                    className="pointer-events-none absolute inset-y-0 left-0 bg-gray-100/15"
                    style={{
                        width: isOverlayShrinking ? '0%' : '100%',
                        transition: `width ${autoDismissMs}ms linear`,
                    }}
                />
            ) : null}
            <div>{message}</div>
            <Button className="flex items-center" {...buttonProps}>
                <FontAwesomeIcon icon={buttonIcon} className="h-4 w-4 fill-current" />
            </Button>
        </li>
    );
}
interface NotificationsContainerProps {
    notificationProps?: Partial<Omit<NotificationProps, 'notification'>>;
}

export function NotificationsContainer({
    notificationProps = {},
}: NotificationsContainerProps): JSX.Element {
    const { notifications } = useNotifications();

    return (
        <ul className="mt-2 list-none" id="notifications-container">
            {notifications.map((notification) => (
                <Notification
                    key={notification.id}
                    notification={notification}
                    {...notification}
                    {...notificationProps}
                />
            ))}
        </ul>
    );
}

export function ConvertDjangoNotifications() {
    const { addNotification } = useNotifications();

    useEffect(() => {
        // #notifications is a script element filled with notifications rendered by Django.
        const notificationsTag = document.getElementById('notifications');

        if (!notificationsTag) {
            return;
        }

        const fragment = document
            .createRange()
            .createContextualFragment(notificationsTag.textContent || '');

        Array.from(fragment.querySelectorAll('.messagelist'))
            .flatMap((list) =>
                Array.from(list.querySelectorAll('li')).map((item) => {
                    const variant = item.getAttribute('class');

                    const notification: Partial<NotificationType> = {
                        // Django has the `info` and `success` notification types which have the
                        // exact same appearance. Here we convert the info type into the success
                        // type in order to prevent unnecessary duplication on the JS side.
                        variant: (variant === 'info' ? 'success' : variant) as NOTIFICATION_VARIANT,
                    };

                    const button = item.getElementsByTagName('button')[0];
                    const buttonA = button.getElementsByTagName('a')[0];

                    if (buttonA) {
                        notification.href = buttonA.getAttribute('href');
                    }

                    button.remove();

                    notification.message = (
                        <span dangerouslySetInnerHTML={{ __html: item.innerHTML }} />
                    );

                    return notification as NotificationType;
                })
            )
            .forEach((notification) => addNotification(notification));
    }, [addNotification]);

    return null;
}
